import { useState, useEffect } from 'react';
import { useAuth } from "../../contexts/AuthContext";
import { db } from "../../firebase";
import { collection, addDoc, doc, updateDoc, arrayUnion, serverTimestamp, getDoc, setDoc } from "firebase/firestore";
import { useNavigate, useParams } from "react-router";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { MobileDatePicker } from '@mui/x-date-pickers';
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import TextField from "@mui/material/TextField";
import Box from "@mui/material/Box";
import * as theme from '../../theme';
import dayjs from 'dayjs';
import StateSelect from '../../components/StateSelect';
import Button from "@mui/material/Button";
import ErrorDisplay from '../../components/ErrorDisplay';
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";

export default function AddClient() {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    dob: '',
    street: '',
    address2: '',
    city: '',
    state: '',
    zip: '',
    concerns: '',
    immediateFamily: false,
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const { practitionerId } = useParams();

  // Check if practitioner has cardOnFile.dateCompleted
  useEffect(() => {
    const checkCardOnFile = async () => {
      try {
        const practitionerRef = doc(db, "practitioners", practitionerId);
        const practitionerSnap = await getDoc(practitionerRef);

        if (practitionerSnap.exists()) {
          const data = practitionerSnap.data();
          // Check if cardOnFile.dateCompleted exists and is set
          if (!data?.onboarding?.cardOnFile?.dateCompleted) {
            setError("A payment method is required before adding clients.");
            navigate("/dashboard");
          }
        } else {
          setError("Practitioner not found.");
          navigate("/dashboard");
        }
      } catch (err) {
        setError("Error checking practitioner details: " + err.message);
        navigate("/dashboard");
      }
    };

    checkCardOnFile();
  }, [practitionerId, navigate]);

  const clearError = () => {
    setError('');
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleDateChange = (value) => {
    setFormData({ ...formData, dob: value });
  };

  const handleStateChange = (e) => {
    setFormData({ ...formData, state: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    try {
      const normalizedEmail = formData.email.toLowerCase().trim();

      const clientData = {
        ...formData,
        email: normalizedEmail,
        firstName: formData.firstName.trim(),
        lastName: formData.lastName.trim(),
        dateCreated: serverTimestamp(),
        dateModified: serverTimestamp(),
        dob: formData.dob ? formData.dob.format('YYYY-MM-DD') : null,
        practitionerId: practitionerId,
        street: formData.street.trim(),
        address2: formData.address2.trim() || null,
        city: formData.city.trim(),
        zip: formData.zip.trim(),
        immediateFamily: formData.immediateFamily,
      };

      // Add client to the clients collection
      const docRef = await addDoc(collection(db, "clients"), clientData);

      // Prepare data for practitioners/{practitionerId}/clients/list
      const practitionerClientData = {
        id: docRef.id,
        firstName: formData.firstName.trim(),
        lastName: formData.lastName.trim(),
        email: normalizedEmail,
      };

      // Update or create the list document in the clients subcollection
      const listRef = doc(db, "practitioners", practitionerId, "clients", "list");
      await setDoc(listRef, {
        clients: arrayUnion(practitionerClientData)
      }, { merge: true });

      // Prepare data for app/clientMap update
      const clientMapData = {
        firstName: formData.firstName.trim(),
        id: docRef.id,
        lastName: formData.lastName.trim(),
        pId: practitionerId,
      };

      // Update app/clientMap document
      const clientMapRef = doc(db, "app", "clientMap");
      await updateDoc(clientMapRef, {
        clients: arrayUnion(clientMapData),
      });

      navigate(`/clients/${docRef.id}`);
    } catch (err) {
      setError("Failed to add client: " + err.message);
    }

    setLoading(false);
  };

  return (
    <div style={theme.Container.basic}>
      <div style={theme.Heading}>Add Client</div>
      <div style={theme.SubHeadingText}>
        {currentUser.role !== 'practitioner' &&
          `Fill out the form below to add a new client to the practitioner's account.`}
        {currentUser.role === 'practitioner' &&
          `Fill out the form below to add a new client to your account.`}
      </div>
      <div style={{ marginTop: 15, marginBottom: 30 }}>
        <div style={{ marginTop: 0, marginBottom: 30 }}>
          <ErrorDisplay
            message={error}
            type={'error'}
            clearMessage={clearError}
          />
        </div>
        <Box
          component="form"
          onSubmit={handleSubmit}
          sx={{ ...theme.FullForm.form, gap: 4 }}
        >
          <TextField
            label="First Name"
            name="firstName"
            value={formData.firstName}
            onChange={handleChange}
            variant="filled"
            required
          />
          <TextField
            label="Last Name"
            name="lastName"
            value={formData.lastName}
            onChange={handleChange}
            variant="filled"
            required
          />
          <TextField
            label="Email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            variant="filled"
            required
          />
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <MobileDatePicker
              label="Date of Birth"
              value={formData.dob !== '' ? formData.dob : null}
              onChange={handleDateChange}
              maxDate={dayjs()}
              openTo="year"
              views={['year', 'month', 'day']}
              yearsOrder="desc"
              slotProps={{
                textField: {
                  variant: "filled",
                },
              }}
            />
          </LocalizationProvider>
          <TextField
            label="Mailing Address"
            name="street"
            value={formData.street}
            onChange={handleChange}
            variant="filled"
            required
          />
          <TextField
            label="Address Line 2"
            name="address2"
            value={formData.address2}
            onChange={handleChange}
            variant="filled"
          />
          <TextField
            label="City"
            name="city"
            value={formData.city}
            onChange={handleChange}
            variant="filled"
            required
          />
          <Box sx={{ display: "flex", width: '100%', gap: 2 }}>
            <StateSelect
              value={formData.state}
              onChange={handleStateChange}
              sx={{ flex: 1 }}
            />
            <TextField
              label="Zip Code"
              name="zip"
              value={formData.zip}
              onChange={handleChange}
              variant="filled"
              required
              sx={{ flex: 1 }}
            />
          </Box>
          <TextField
            label="Specific Concerns/Diagnosis"
            name="concerns"
            value={formData.concerns}
            onChange={handleChange}
            variant="filled"
            multiline
            rows={4}
            fullWidth
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={formData.immediateFamily}
                onChange={(e) => setFormData({ ...formData, immediateFamily: e.target.checked })}
              />
            }
            label={
              <div><strong>Immediate Family Member of Practitioner?</strong><br />Qualifies for Practitioner Scan Rate (ex. Spouse, Children, Pets)</div>
            }
          />
          <Box sx={{ display: "flex", width: '100%', gap: 2 }}>
            <Button
              variant="contained"
              color="secondary"
              sx={{ flex: 1 }}
              onClick={() => navigate('/dashboard')}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              color="primary"
              disabled={loading}
              sx={{ flex: 3 }}
            >
              {loading ? "Adding..." : "Add Client"}
            </Button>
          </Box>
        </Box>
      </div>
    </div>
  );
}